import {
  Component,
  computed,
  forwardRef,
  input,
  output,
  signal,
  untracked,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

let nextToggleId = 0;

@Component({
  selector: "ml-toggle",
  standalone: true,
  imports: [],
  host: { class: "block" },
  templateUrl: "./toggle.component.html",
  styleUrl: "./toggle.component.css",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true,
    },
  ],
})
export class ToggleComponent implements ControlValueAccessor {
  // Static inputs (for non-form / "necessary" toggle use)
  checked = input<boolean>(false);
  disabled = input<boolean>(false);
  hasError = input<boolean>(false);
  labelPosition = input<"before" | "after">("after");

  // Emits the new boolean value on each toggle
  toggled = output<boolean>();

  // Module-scoped unique id for label association
  protected readonly uid = `ml-toggle-${++nextToggleId}`;

  // Computed class for the track div (includes focus-ring color variant)
  protected readonly trackClass = computed(() => {
    const focusRing = this.hasError()
      ? "peer-focus:ring-red-400"
      : "peer-focus:ring-primary-muted";
    return [
      "relative shrink-0 w-9 h-5 bg-bg",
      "peer-focus:outline-none peer-focus:ring-2",
      focusRing,
      "rounded-full peer",
      "peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full",
      "peer-checked:after:border-surface",
      "after:content-[''] after:absolute after:top-[2px] after:start-[2px]",
      "after:bg-bg after:border-border-strong after:border",
      "after:rounded-full after:h-4 after:w-4 after:transition-all",
      "peer-checked:bg-primary",
    ].join(" ");
  });

  // CVA internal state
  protected value = signal<boolean>(false);
  protected isDisabled = signal<boolean>(false);

  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(val: boolean): void {
    this.value.set(!!val);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    untracked(() => this.isDisabled.set(isDisabled));
  }

  // Template handler — reads native checkbox value
  protected onToggle(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.value.set(checked);
    this.onChange(checked);
    this.onTouched();
    this.toggled.emit(checked);
  }
}
